/**
 * Live Apple Place Details recall for admin review (Part 2D).
 *
 * Calls the same `apple-places-search` Edge Function the main app uses
 * (`lib/appleVenueSearch.ts → recoverAppleCoordinates`). No new credentials in
 * admin env: Apple Maps Server credentials (APPLE_TEAM_ID / APPLE_KEY_ID /
 * APPLE_PRIVATE_KEY) live in Supabase Edge env, never in browser code.
 *
 * Requirements:
 *   - Admin user must be logged in (Edge function requires a Bearer token).
 *   - The admin's deployed origin must be in the Edge function's
 *     `ADMIN_WEB_ORIGIN` env (comma-separated list). If unset, browser calls
 *     are blocked by CORS — flagged in the migration / deploy notes.
 *
 * Apple-derived data returned by this call is RENDER-ONLY. Do NOT persist
 * name / address / coordinates to any database column. Only the apple_place_id
 * is allowed in storage.
 */
import { supabase } from './supabase';

export type AppleRecallResult = {
  name: string;
  formattedAddress: string;
  coordinate: { latitude: number; longitude: number } | null;
};

export async function recallAppleVenueInfo(applePlaceId: string): Promise<AppleRecallResult> {
  const trimmed = applePlaceId?.trim();
  if (!trimmed) throw new Error('apple_place_id missing');

  const { data, error } = await supabase.functions.invoke('apple-places-search', {
    body: { applePlaceId: trimmed },
  });

  if (error) {
    // Surface a precise reason for inline display — never a generic toast.
    const detail = error.message ?? String(error);
    if (detail.toLowerCase().includes('cors')) {
      throw new Error(
        `Apple lookup blocked by CORS — admin origin missing from Edge env ADMIN_WEB_ORIGIN.`,
      );
    }
    if (detail.toLowerCase().includes('unauthorized')) {
      throw new Error('Apple lookup failed: admin session expired — sign in again.');
    }
    if (detail.toLowerCase().includes('rate')) {
      throw new Error('Apple lookup rate-limited — wait a minute and retry.');
    }
    throw new Error(`Apple lookup failed: ${detail}`);
  }

  type EdgeRow = {
    appleId?: string | null;
    name?: string | null;
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
  const row = (data as { results?: EdgeRow[] } | null)?.results?.[0];
  if (!row) {
    throw new Error('Apple returned no place for this id (deleted or not visible).');
  }

  const lat = typeof row.latitude === 'number' && Number.isFinite(row.latitude) ? row.latitude : null;
  const lng = typeof row.longitude === 'number' && Number.isFinite(row.longitude) ? row.longitude : null;
  const coordinate = lat != null && lng != null ? { latitude: lat, longitude: lng } : null;

  return {
    name: row.name?.trim() ?? '',
    formattedAddress: row.address?.trim() ?? '',
    coordinate,
  };
}

/** Convert a name to Title Case for the edit form. */
export function toTitleCase(s: string): string {
  return s
    .trim()
    .split(/\s+/)
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}
